import {Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from './studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {useCanChangeStudyableAssignmentStudiesQuery} from './studyable/useCanChangeStudyableAssignmentQuery';
import {StudyAssociation} from './StudyableDAO';
import StudySearchInput, {StudySearchControlledItemsProps, StudySearchItem} from './StudySearchInput';

export interface StudyAssociationInputProps {
  studySearchControlledItemsProps: StudySearchControlledItemsProps;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  setIsAutoLinkingChecked: (e: InputChangeEvent) => void;
  setAutoLinkingStudyId: (studyId: string) => void;
  displayPermissionWarning?: boolean;
  hasInitialAutoLinkingStudy: boolean;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
}

export default function StudyAssociationInput({
  studySearchControlledItemsProps,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  setIsAutoLinkingChecked,
  setAutoLinkingStudyId,
  displayPermissionWarning,
  hasInitialAutoLinkingStudy,
  autoLinkingStudy,
}: StudyAssociationInputProps): JSX.Element {
  const selectedStudies = studySearchControlledItemsProps.selectedItems;
  const isMultipleSelectedItems = selectedStudies.length > 1;
  const filteredStudies = selectedStudies.filter((study) => !invalidItemNames.includes(study.name));
  return (
    <>
      <StudySearchInput {...studySearchControlledItemsProps} shouldAllowMultiple={true} />
      <>
        {displayPermissionWarning && invalidItemNames.length > 0 && (
          <div className={css(styles.warningBanner)}>
            <ChangeStudyableAssignmentPermissionWarningBanner
              warningOriginType={ChangeStudyableAssignmentPermissionOriginType.STUDY}
              invalidStudyNames={invalidItemNames}
            />
          </div>
        )}
        {autoLinkingStudy && hasInitialAutoLinkingStudy && (
          <div className={css(styles.autoLinkingCheckbox)}>
            <CheckboxInput
              id="study-auto-link-checkbox"
              label={
                <div>
                  Automatically link items created in this entry to <strong>{autoLinkingStudy.name}</strong>
                </div>
              }
              disabled={isAutoLinkingDisabled}
              checked={isAutoLinkingChecked}
              onChange={setIsAutoLinkingChecked}
            />
          </div>
        )}
        {BenchlingConfig.STUDIES_ENTRY_AUTO_LINKING_EXTENSIONS &&
          autoLinkingStudy &&
          !hasInitialAutoLinkingStudy && (
            <div className={css(styles.autoLinkingSection)}>
              <>
                <Checkbox
                  className={css(styles.autoLinkingCheckbox)}
                  id="study-auto-link-checkbox"
                  checked={isAutoLinkingChecked}
                  onChange={setIsAutoLinkingChecked}
                  disabled={isAutoLinkingDisabled}
                  label={
                    <>
                      Automatically link items created in this entry to{' '}
                      {!isMultipleSelectedItems && <strong>{autoLinkingStudy.name}</strong>}
                    </>
                  }
                />
                {isMultipleSelectedItems && (
                  <span className={css(styles.autoLinkingDropdown)}>
                    <SelectDropdown
                      ariaLabel="Auto-linking study dropdown"
                      displayName={autoLinkingStudy.name}
                      menu={
                        <Menu value={autoLinkingStudy.id} width={Menu.WIDTHS.FULL}>
                          {filteredStudies.map((study) => (
                            <Option
                              key={study.id}
                              value={study.id}
                              onSelect={() => {
                                setAutoLinkingStudyId(study.id);
                              }}
                            >
                              {study.name}
                            </Option>
                          ))}
                        </Menu>
                      }
                    />
                  </span>
                )}
              </>
            </div>
          )}
      </>
    </>
  );
}

export function useStudyAssociationInputProps({
  initialStudySearchItems,
  displayPermissionWarning,
  initialAutoLinkingStudy,
}: {
  initialStudySearchItems: Array<StudySearchItem>;
  displayPermissionWarning?: boolean;
  initialAutoLinkingStudy?: {
    id: string;
    name: string;
  };
}): StudyAssociationInputProps {
  const hasInitialAutoLinkingStudy = initialAutoLinkingStudy !== undefined;
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialStudySearchItems);
  const [autoLinkingStudyId, setAutoLinkingStudyId] = useState<string | undefined>(
    initialAutoLinkingStudy?.id ?? studySearchItems[0]?.id
  );
  const studyIds = studySearchItems.map((item) => item.id);
  const autoLinkingStudy =
    initialAutoLinkingStudy ?? studySearchItems.find((study) => study.id === autoLinkingStudyId);
  const isAutoLinkingStudySelected = autoLinkingStudy !== undefined && studyIds.includes(autoLinkingStudy.id);
  const [isAutoLinkingChecked, setIsAutoLinkingChecked] = useState(
    hasInitialAutoLinkingStudy && isAutoLinkingStudySelected
  );
  // Get studies missing the change studyable assignment permission
  const studyNamesToStudyablePermission = useCanChangeStudyableAssignmentStudiesQuery(studyIds);
  const invalidItemNames = Array.from(studyNamesToStudyablePermission.entries())
    .filter(([, hasPermission]) => !hasPermission)
    .map(([name]) => name);
  const isAutoLinkingDisabled =
    (hasInitialAutoLinkingStudy && !isAutoLinkingStudySelected) || invalidItemNames.length > 0;

  // We want to uncheck the auto-linking checkbox if the auto-linking study is deselected
  useEffect(() => {
    if (!isAutoLinkingStudySelected) {
      setIsAutoLinkingChecked(false);
    }
  }, [isAutoLinkingStudySelected]);

  const handleStudySearchItemsChange = (selectedStudies: Array<StudySearchItem>): void => {
    setStudySearchItems(selectedStudies);
    if (!autoLinkingStudy || !selectedStudies.some((study) => study.id === autoLinkingStudy.id)) {
      setAutoLinkingStudyId(selectedStudies[0]?.id);
    }
  };

  return {
    studySearchControlledItemsProps: {
      selectedItems: studySearchItems,
      onChange: handleStudySearchItemsChange,
    },
    invalidItemNames,
    isAutoLinkingChecked,
    isAutoLinkingDisabled,
    setIsAutoLinkingChecked: (e: InputChangeEvent) => {
      setIsAutoLinkingChecked(e.target.checked);
    },
    setAutoLinkingStudyId: (studyId: string) => {
      setAutoLinkingStudyId(studyId);
    },
    displayPermissionWarning,
    hasInitialAutoLinkingStudy,
    autoLinkingStudy,
  };
}

export function getStudyAssociations(
  studyAssociationInputProps: StudyAssociationInputProps
): Array<StudyAssociation> {
  return studyAssociationInputProps.studySearchControlledItemsProps.selectedItems.map((item) => ({
    studyId: item.id,
    isAutoLinking:
      studyAssociationInputProps.isAutoLinkingChecked &&
      item.id === studyAssociationInputProps.autoLinkingStudy?.id,
  }));
}

const styles = StyleSheet.create({
  warningBanner: {
    marginTop: 5,
  },
  autoLinkingSection: {
    alignItems: 'flex-start',
    display: 'flex',
    marginTop: 5,
  },
  autoLinkingCheckbox: {
    marginTop: 5,
  },
  autoLinkingDropdown: {
    flex: 1,
    marginLeft: 4,
    minWidth: 0,
  },
});
